/**
 * Owner-admin API client for the ZCD Conversion tab.
 * Talks to /api/admin/v2/zcd-convert/* — superuser-only per backend
 * IsSuperuser permission.
 *
 * Backend module: apps/admin_tools/owner_api_zcd_convert.py.
 * The same two converter scripts run inside the standalone Zen Ingest
 * desktop tool.
 */
import api from "./axios";

const BASE = "/api/admin/v2/zcd-convert";

export const listZcdBatches = () => api.get(`${BASE}/`);

export const getZcdBatch = (batchId) => api.get(`${BASE}/${batchId}/`);

export const deleteZcdBatch = (batchId) => api.delete(`${BASE}/${batchId}/`);

/**
 * Upload a tournament ball-by-ball file and run the picked converter.
 * source ∈ {"OPTA", "VENDOR"}. onProgress(percent) is invoked for
 * upload progress updates.
 *
 * `scorecard` is optional — an xlsx workbook with one sheet per match
 * used to cross-verify the converted data. Never blocks conversion.
 */
export const createZcdBatch = ({ name, source, file, scorecard, onProgress }) => {
  const form = new FormData();
  if (name) form.append("name", name);
  form.append("source", source);
  form.append("file", file);
  if (scorecard) form.append("scorecard", scorecard);
  return api.post(`${BASE}/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded * 100) / evt.total));
      }
    },
    // Conversion can take a while on tournament-sized inputs.
    timeout: 15 * 60 * 1000,
  });
};

/**
 * Attach (or replace) the optional scorecard workbook on an existing
 * batch. Reconciles the scorecard against the already-converted output
 * and returns the fresh verification report.
 */
export const attachZcdScorecard = ({ batchId, file, onProgress }) => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`${BASE}/${batchId}/scorecard/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded * 100) / evt.total));
      }
    },
    timeout: 10 * 60 * 1000,
  });
};

/** Remove the scorecard + verification report from a batch. */
export const deleteZcdScorecard = (batchId) =>
  api.delete(`${BASE}/${batchId}/scorecard/`);

/** Absolute-ish URL for downloading a single per-match xlsx. */
export const fileDownloadUrl = (batchId, filename) =>
  `${BASE}/${batchId}/files/${encodeURIComponent(filename)}/`;

/**
 * Bundle every converted xlsx for the batch into a zip named
 * `<folderName>.zip`. Returns the axios promise; caller triggers the
 * browser download via a temp <a>.
 */
export const downloadZcdZip = (batchId, folderName) => {
  const form = new FormData();
  form.append("folder_name", folderName || "");
  return api.post(`${BASE}/${batchId}/zip/`, form, {
    responseType: "blob",
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Merge every per-match workbook into ONE xlsx (each sheet gets a
 * `match_id` column prepended). Returned as a downloadable blob named
 * `<folderName>.xlsx`. Long timeout — merging 30+ workbooks can take
 * a while on tournament-sized inputs.
 */
export const downloadZcdCombined = (batchId, folderName) => {
  const form = new FormData();
  form.append("folder_name", folderName || "");
  return api.post(`${BASE}/${batchId}/combined/`, form, {
    responseType: "blob",
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 10 * 60 * 1000,
  });
};
